import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Eye, Clock } from "@phosphor-icons/react";

export default function AdminPayments() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [preview, setPreview] = useState(null);

    const load = () =>
        api
            .get(`/payments${filter ? `?status=${filter}` : ""}`)
            .then(({ data }) => setRows(data));
    useEffect(() => {
        load();
    }, [filter]);

    const verify = async (id, status) => {
        try {
            await api.put(`/payments/${id}/verify`, { status, note: "" });
            toast.success(
                status === "approved"
                    ? "Approved — credits unlocked"
                    : "Rejected",
            );
            load();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        }
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10"
            data-testid="admin-payments"
        >
            <div className="relative overflow-hidden mb-6 p-6 g-sunset text-white shadow-glow-pink">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                <div className="relative flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-white/70">
                            Admin
                        </div>
                        <h1 className="font-display font-black text-4xl tracking-tighter">
                            Payments
                        </h1>
                    </div>
                    <div className="flex gap-1 bg-white/10 backdrop-blur border border-white/20">
                        {["", "pending", "approved", "rejected"].map((f) => (
                            <button
                                key={f || "all"}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-2 text-xs uppercase tracking-widest ${filter === f ? "bg-white text-amber-700 font-bold" : "text-white/90 hover:bg-white/10"}`}
                            >
                                {f || "All"}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="card-surface overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b">
                        <tr className="text-xs uppercase tracking-widest text-zinc-500">
                            <th className="text-left p-3">User</th>
                            <th className="text-left p-3">Package</th>
                            <th className="text-left p-3">Amount</th>
                            <th className="text-left p-3">Ref</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="p-6 text-center text-zinc-500"
                                >
                                    No payments
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr
                                key={p.id}
                                className="border-b border-zinc-100"
                                data-testid={`payrow-${p.id}`}
                            >
                                <td className="p-3">
                                    <div className="font-medium">
                                        {p.user_name}
                                    </div>
                                    <div className="text-xs text-zinc-500 font-mono">
                                        {p.user_email}
                                    </div>
                                </td>
                                <td className="p-3">
                                    <div>{p.package_name}</div>
                                    <div className="text-xs text-zinc-500 font-mono">
                                        {p.card_limit} credits
                                    </div>
                                </td>
                                <td className="p-3 font-mono">
                                    ₹{Number(p.amount ?? 0).toFixed(0)}
                                    {p.coupon_code && (
                                        <div className="text-xs text-emerald-700">
                                            {p.coupon_code} · −₹
                                            {Number(p.discount_amount ?? 0).toFixed(0)}
                                        </div>
                                    )}
                                </td>
                                <td className="p-3 font-mono text-xs">
                                    {p.transaction_ref || "—"}
                                </td>
                                <td className="p-3 text-xs text-zinc-500">
                                    {new Date(p.created_at).toLocaleString()}
                                </td>
                                <td className="p-3">
                                    {p.status === "pending" && (
                                        <span className="text-amber-600 text-xs font-bold flex items-center gap-1">
                                            <Clock size={13} /> PENDING
                                        </span>
                                    )}
                                    {p.status === "approved" && (
                                        <span className="text-green-600 text-xs font-bold flex items-center gap-1">
                                            <CheckCircle
                                                size={13}
                                                weight="fill"
                                            />{" "}
                                            APPROVED
                                        </span>
                                    )}
                                    {p.status === "rejected" && (
                                        <span className="text-red-600 text-xs font-bold flex items-center gap-1">
                                            <XCircle size={13} weight="fill" />{" "}
                                            REJECTED
                                        </span>
                                    )}
                                </td>
                                <td className="p-3 text-right space-x-1">
                                    {p.screenshot_base64 && (
                                        <button
                                            className="btn-ghost"
                                            onClick={() =>
                                                setPreview(p.screenshot_base64)
                                            }
                                            data-testid={`preview-${p.id}`}
                                        >
                                            <Eye size={14} />
                                        </button>
                                    )}
                                    {p.status === "pending" && (
                                        <>
                                            <button
                                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs"
                                                onClick={() =>
                                                    verify(p.id, "approved")
                                                }
                                                data-testid={`approve-${p.id}`}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs"
                                                onClick={() =>
                                                    verify(p.id, "rejected")
                                                }
                                                data-testid={`reject-${p.id}`}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {preview && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/80 flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                >
                    <img
                        src={preview}
                        alt="Payment"
                        className="max-h-[90vh] max-w-[90vw] border border-white"
                    />
                </div>
            )}
        </div>
    );
}
